import React, { useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifInput } from '../input/input';
import { MotifButton } from '../../button/button';
import { ReactComponent as ContentIcRemove24px } from '../../../assets/icons/content/ic_remove_24px.svg';
import { ReactComponent as ContentIcAdd24px } from '../../../assets/icons/content/ic_add_24px.svg';
import { FormFieldContext } from '../form-field/form-field.context';

let nextUniqueId;
nextUniqueId = 0;
const MAX_VALUE = 99;
const MIN_VALUE = -99;

const defaultIcons = {
  decrease: <ContentIcRemove24px />,
  increase: <ContentIcAdd24px />
};

/**
 * @typedef Props
 * @prop {number | string} [value]
 * @prop {string} [className]
 * @prop {(event: React.SyntheticEvent) => void} [onChange]
 * @prop {{decrease: React.ReactNode, increase: React.ReactNode}} [icons]
 * @prop {string} [id]
 * @prop {boolean} [disabled]
 * @prop {number | string} [step]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifNumericInput = (props) => {
  const {
    value = '',
    onChange = () => null,
    icons: userIcons = {},
    id = '',
    disabled = false,
    step = 1,
    className = '',
    ...other
  } = props;
  const stepValue = Math.floor(Math.abs(step)) || 1;
  const valueRef = useRef(value);
  const formFieldContext = useContext(FormFieldContext);
  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  useEffect(() => {
    formFieldContext.setFormElementType('numeric');
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-numeric-${nextUniqueId++}`);
  }, []);

  const changeNumber = action => {
    let valueChanged;
    let actionCondition;

    if (action === 'increase') {
      valueChanged = Number(value) + stepValue;
      actionCondition = valueChanged > MAX_VALUE;
    } else if (action === 'decrease') {
      valueChanged = Number(value) - stepValue;
      actionCondition = valueChanged < MIN_VALUE;
    }

    onChange({
      target: {
        value: actionCondition ? Number(value) : valueChanged.toString()
      }
    });
    valueRef.current = valueChanged;
  };

  const handleKeyDown = event => {
    const arrowUp = 38;
    const arrowDown = 40;

    switch (event.keyCode) {
      case arrowUp:
        changeNumber('increase');
        break;

      case arrowDown:
        changeNumber('decrease');
        break;

      default:
        break;
    }
  };

  const getCorrectValueStep = valueCorrected => {
    const centerNumber = Number(valueCorrected) - Number(valueRef.current);
    if (centerNumber % stepValue === 0) {
      onChange({ target: { value: valueCorrected } });
      valueRef.current = valueCorrected;
    } else {
      onChange({ target: { value: valueRef.current } });
    }
  };

  const handleChange = event => {
    const pattern = /^[-]?\d{0,2}$/;
    const match = pattern.test(event.target.value);

    if (match) {
      onChange({ target: { value: event.target.value } });
    }
  };

  const handleBlur = () => {
    if (isNaN(value) || value === '') {
      onChange({ target: { value: valueRef.current } });
    } else {
      getCorrectValueStep(Math.round(value));
    }
  };

  const handleClickDown = event => {
    event.preventDefault();
    changeNumber('decrease');
  };

  const handleClickUp = event => {
    event.preventDefault();
    changeNumber('increase');
  };

  const classes = classNames({
    'motif-wrapper-numeric': true,
    [className]: true
  });

  return (
    <div className={classes} data-motif-numeric-input>
      <MotifButton
        className="motif-numeric-button"
        onClick={handleClickDown}
        disabled={Number(value) <= MIN_VALUE + stepValue || disabled}
        aria-label="decrease value"
        aria-live="assertive"
      >
        {icons.decrease}
      </MotifButton>
      <MotifInput
        max={MAX_VALUE}
        min={MIN_VALUE}
        step={stepValue}
        id={formFieldContext.id}
        className="motif-numeric-input"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-live="assertive"
        role="alert"
        hideClearButton
        disabled={disabled}
        {...other}
      />
      <MotifButton
        className="motif-numeric-button"
        onClick={handleClickUp}
        disabled={Number(value) >= MAX_VALUE - stepValue || disabled}
        aria-label="increase value"
        aria-live="assertive"
      >
        {icons.increase}
      </MotifButton>
    </div>
  );
};

MotifNumericInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  icons: PropTypes.shape({
    decrease: PropTypes.node,
    increase: PropTypes.node
  })
};

export { MotifNumericInput };
